import { formatExitReason } from './exitReasonLabel';
// frontend/src/utils/simChartOverlay.js
import axios from 'axios';

// 청산 사유 한글 매핑
const EXIT_REASON_LABELS = {
  stop_loss: '손절',
  break_even: '본청',
  power_zero: '강도 0 청산 ',
  market_end: '장마감 청산',
  power_80: '강도 80% 익절',
  premarket_profit_over_100p: '프리장 100p 수익 익절',
  price_over_25p_during_auto_trading_blocked: '25p이탈 진입차단',
};

const mapExitReason = (reason, event) =>
  formatExitReason(reason, event, EXIT_REASON_LABELS);

// Highcharts line series에서 null로 선을 끊기 위해 point 객체 + null을 섞어서 사용
function buildSegmentLineData(segments) {
  const data = [];
  for (const s of segments) {
    data.push({ x: s.x1_ms, y: s.price, boxline_id: s.boxline_id });
    data.push({ x: s.x2_ms, y: s.price, boxline_id: s.boxline_id });
    data.push(null); // break
  }
  return data;
}

function buildScatterData(points) {
  return points.map((p) => {
    const x =
      (typeof p.ts_ms === 'number' && p.ts_ms) ||
      (p.event_ts_kst ? Date.parse(p.event_ts_kst) : undefined) ||
      (p.t ? Date.parse(p.t) : undefined);
    const y =
      (typeof p.price === 'number' ? p.price : undefined) ??
      (typeof p.close_box === 'number' ? p.close_box : undefined);
    return { x, y, ...p, price: y };
  });
}

export async function fetchSimChartData({
  apiUrl,
  runId,
  startMs,
  endMs,
  includeBoxlines = true,
  includeOrders = false,
  includeRisk = true,
}) {
  const params = {
    include_boxlines: includeBoxlines,
    include_orders: includeOrders,
    include_risk: includeRisk,
  };

  // xAxis extremes를 그대로 ISO로 넘기면(UTC Z) 서버에서는 timestamptz 비교로 문제 없음
  if (typeof startMs === 'number')
    params.start_kst = new Date(startMs).toISOString();
  if (typeof endMs === 'number') params.end_kst = new Date(endMs).toISOString();

  const { data } = await axios.get(`${apiUrl}/sim/runs/${runId}/chart_data`, {
    params,
  });
  return data;
}

/**
 * chart_data 응답을 Highcharts series로 변환
 * - boxLineSegments: TOP/BOT 2개 line series로 묶음(성능)
 * - mostLineEvents: scatter (promote/demote를 marker로 구분)
 * - entries/exits: scatter
 * - orderFills: scatter(옵션)
 */
export function toHighchartsOverlaySeries(chartData) {
  const topSegs = (chartData.boxLineSegments || []).filter((s) => s.is_top);
  const botSegs = (chartData.boxLineSegments || []).filter((s) => !s.is_top);

  // const mostPromote = (chartData.mostLineEvents || []).filter(
  //   (e) => e.event_type === 'promote'
  // );
  // const mostDemote = (chartData.mostLineEvents || []).filter(
  //   (e) => e.event_type === 'demote'
  // );

  const series = [];

  // 1) BoxLines TOP/BOT
  series.push({
    id: 'sim-boxlines-top',
    name: 'SIM BoxLines TOP',
    type: 'line',
    data: buildSegmentLineData(topSegs),
    lineWidth: 1,
    enableMouseTracking: false,
    turboThreshold: 0,
    zIndex: 2,
  });

  series.push({
    id: 'sim-boxlines-bot',
    name: 'SIM BoxLines BOT',
    type: 'line',
    data: buildSegmentLineData(botSegs),
    lineWidth: 1,
    enableMouseTracking: false,
    turboThreshold: 0,
    zIndex: 2,
  });

  // 2) MostLine events (promote/demote)
  // series.push({
  //   id: 'sim-mostline-promote',
  //   name: 'SIM MostLine Promote',
  //   type: 'scatter',
  //   data: buildScatterData(
  //     mostPromote.map((e) => ({ ...e, price: e.price ?? e.close_box }))
  //   ),
  //   marker: { symbol: 'circle', radius: 5 },
  //   states: { inactive: { opacity: 1, enabled: false } },
  //   tooltip: {
  //     pointFormatter() {
  //       return `
  //         <div>
  //           <div><b>진입라인 승격</b></div>
  //           <div>boxline_id: ${this.boxline_id}</div>
  //           <div>is_top: ${this.is_top}</div>
  //           <div>price: ${this.price}</div>
  //         </div>
  //       `;
  //     },
  //   },
  //   turboThreshold: 0,
  //   zIndex: 5,
  // });

  // series.push({
  //   id: 'sim-mostline-demote',
  //   name: 'SIM MostLine Demote',
  //   type: 'scatter',
  //   data: buildScatterData(mostDemote.map((e) => ({ ...e, price: e.price }))),
  //   marker: { symbol: 'triangle-down', radius: 5 },
  //   states: { inactive: { opacity: 1, enabled: false } },
  //   tooltip: {
  //     pointFormatter() {
  //       return `
  //         <div>
  //           <div><b>진입라인 해제</b></div>
  //           <div>boxline_id: ${this.boxline_id}</div>
  //           <div>is_top: ${this.is_top}</div>
  //           <div>price: ${this.price}</div>
  //         </div>
  //       `;
  //     },
  //   },
  //   turboThreshold: 0,
  //   zIndex: 5,
  // });

  // 3) Entries / Exits
  series.push({
    id: 'sim-entries',
    name: '진입(SIM)',
    type: 'scatter',
    data: buildScatterData(chartData.entries || []),
    marker: { symbol: 'triangle', radius: 10 },
    states: { inactive: { opacity: 1, enabled: false } },
    tooltip: {
      pointFormatter() {
        return (
          `<b>방향</b>: ${this.direction == 'LONG' ? '매수' : '매도'}</br>` +
          `<b>가격</b>:  ${this.price}`
        );
      },
    },
    turboThreshold: 0,
    zIndex: 6,
  });

  series.push({
    id: 'sim-exits',
    name: '청산(SIM)',
    type: 'scatter',
    data: buildScatterData(chartData.exits || []),
    marker: { symbol: 'triangle-down', radius: 10 },
    states: { inactive: { opacity: 1, enabled: false } },
    tooltip: {
      pointFormatter() {
        return (
          `청산 사유: ${mapExitReason(this.reason, this) ?? ''}</br>` +
          `총 수익: ${this.profit ?? ''}</br>` +
          `가격: ${this.price}`
        );
      },
    },
    turboThreshold: 0,
    zIndex: 6,
  });

  // 4) Order fills (옵션)
  if (Array.isArray(chartData.orderFills) && chartData.orderFills.length) {
    series.push({
      id: 'sim-order-fills',
      name: '주문체결(SIM)',
      type: 'scatter',
      data: buildScatterData(chartData.orderFills),
      marker: { symbol: 'circle', radius: 5 },
      states: { inactive: { opacity: 1, enabled: false } },
      tooltip: {
        pointFormatter() {
          return (
            `방향: ${this.side == 'BUY' ? '매수' : '매도'}<br/>` +
            `가격: ${this.price}<br/>` +
            `수량: ${this.qty}`
          );
        },
      },
      turboThreshold: 0,
      zIndex: 4,
    });
  }

  return series;
}

/**
 * chart에 오버레이 적용(있으면 setData, 없으면 addSeries)
 */
export function applyOverlaySeries(chart, overlaySeries) {
  if (!chart) return;

  for (const s of overlaySeries) {
    const exist = chart.get(s.id);
    if (exist) {
      exist.setData(s.data, false, false, false);
      // tooltip/marker 등 옵션 변경이 필요하면 update 사용(비용 있음)
      // exist.update({ marker: s.marker, tooltip: s.tooltip }, false);
    } else {
      chart.addSeries(s, false);
    }
  }
  chart.redraw();
}

/**
 * 오버레이 제거
 */
export function removeOverlaySeries(chart) {
  if (!chart) return;
  const ids = [
    'sim-boxlines-top',
    'sim-boxlines-bot',
    'sim-mostline-promote',
    'sim-mostline-demote',
    'sim-entries',
    'sim-exits',
    'sim-order-fills',
  ];
  for (const id of ids) {
    const s = chart.get(id);
    if (s) s.remove(false);
  }
  chart.redraw();
}
